import {
  DownOutlined,
  SearchOutlined,
  UpOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Form, Space, ConfigProvider, theme } from 'antd';
import type { FormInstance } from 'antd/es/form';
import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const { useToken } = theme;

export interface CollapsibleFilterItem {
  key: string;
  label?: React.ReactNode;
  node: React.ReactNode;
  // 可以根据需要传入自定义样式，比如宽度
  style?: React.CSSProperties;
}

interface CollapsibleFilterFormProps<T extends object> {
  form: FormInstance<T>;
  items: CollapsibleFilterItem[];
  defaultVisibleCount?: number;
  onFinish: (values: T) => void;
  onReset: () => void;
  primaryAction?: React.ReactNode;
  submitText?: string;
  style?: React.CSSProperties;
}

function CollapsibleFilterForm<T extends object>({
  form,
  items,
  defaultVisibleCount = 3,
  onFinish,
  onReset,
  primaryAction,
  submitText = '查询',
  style,
}: CollapsibleFilterFormProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const { token } = useToken();

  const shouldCollapse = items.length > defaultVisibleCount;
  
  const primaryItems = useMemo(() => items.slice(0, defaultVisibleCount), [items, defaultVisibleCount]);
  const overflowItems = useMemo(() => items.slice(defaultVisibleCount), [items, defaultVisibleCount]);

  const toggleExpand = () => setExpanded(!expanded);

  const actionButtons = (
    <Space size={10}>
      <Button
        htmlType="submit"
        type="primary"
        icon={<SearchOutlined />}
        style={{
          borderRadius: 8,
          height: 36,
          paddingInline: 18,
          boxShadow: `0 4px 12px ${token.colorPrimary}30`,
          fontWeight: 500,
        }}
      >
        {submitText}
      </Button>
      <Button
        onClick={onReset}
        icon={<ReloadOutlined />}
        style={{
          borderRadius: 8,
          height: 36,
          paddingInline: 14,
          color: token.colorTextSecondary,
          borderColor: token.colorBorderSecondary,
        }}
      >
        重置
      </Button>
      {shouldCollapse && (
        <Button
          type="text"
          onClick={toggleExpand}
          style={{
            height: 36,
            color: token.colorPrimary,
            paddingInline: 4,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span>{expanded ? '收起' : '展开'}</span>
          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            style={{ display: 'flex' }}
          >
            <DownOutlined style={{ fontSize: 11 }} />
          </motion.span>
        </Button>
      )}
    </Space>
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0,
          },
          Input: { controlHeight: 36, borderRadius: 8 },
          Select: { controlHeight: 36, borderRadius: 8 },
          DatePicker: { controlHeight: 36, borderRadius: 8 },
        },
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '16px 20px',
          boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          ...style,
        }}
      >
        <Form<T>
          form={form}
          onFinish={onFinish}
          layout="horizontal"
          autoComplete="off"
        >
          {/* 第一行：紧凑排列 */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '12px 24px',
            }}
          >
            {primaryItems.map((item) => (
              <div key={item.key} style={{ flex: '0 0 auto' }}>
                <Form.Item
                  label={
                    <span style={{ color: token.colorTextSecondary, fontWeight: 500, fontSize: 13 }}>
                      {item.label}
                    </span>
                  }
                  name={item.key as never}
                  labelCol={{ flex: '0 0 auto' }}
                  wrapperCol={{ flex: '1 1 auto' }}
                >
                   {item.node}
                </Form.Item>
              </div>
            ))}

            {/* 按钮区：自动占据剩余空间，但保持靠右或紧跟 */}
            <div
              style={{
                flex: '1 1 auto',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minWidth: 200, // 保证按钮有足够空间不换行
              }}
            >
              <div style={{ flexShrink: 0 }}>{actionButtons}</div>
              {primaryAction && (
                <div style={{ flexShrink: 0, marginLeft: 16 }}>
                  <Space size={10}>{primaryAction}</Space>
                </div>
              )}
            </div>
          </div>

          {/* 展开内容 */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                style={{ overflow: 'hidden' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '12px 24px',
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: `1px dashed ${token.colorBorderSecondary}`,
                  }}
                >
                  {overflowItems.map((item) => (
                    <div key={item.key} style={{ flex: '0 0 auto' }}>
                      <Form.Item
                        label={
                          <span style={{ color: token.colorTextSecondary, fontWeight: 500, fontSize: 13 }}>
                            {item.label}
                          </span>
                        }
                        name={item.key as never}
                        labelCol={{ flex: '0 0 auto' }}
                        wrapperCol={{ flex: '1 1 auto' }}
                      >
                         {item.node}
                      </Form.Item>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Form>
      </div>
    </ConfigProvider>
  );
}

export default CollapsibleFilterForm;
